import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { FaPlay, FaPause } from 'react-icons/fa';
import PropTypes from 'prop-types';

// Global flag to track if user has provided gesture permission in this session
let globalSafariUserGesturePermission = false;

const AudioVisualizer = forwardRef(({ audioUrl, audioBytes: _audioBytes, autoplay = false, onPlay, onPause, onAudioEnd, disabled = false }, ref) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, _setLoading] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  const [hasUserGesture, setHasUserGesture] = useState(false);

  // Update progress when currentTime or duration changes
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    pause: () => {
      pauseAudio();
    },
    stop: () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
        setCurrentTime(0);
        if (onPause && typeof onPause === 'function') {
          onPause();
        }
      }
    },
    isPlaying: () => isPlaying
  }));

  useEffect(() => {
    if (audioUrl && autoplay && !disabled) {
      // Check if browser is Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isSafari) {
        // For Safari, check if we have global permission or local permission
        if (hasUserGesture || globalSafariUserGesturePermission) {
          console.log('Safari: Using existing user gesture permission for autoplay');
          playAudio();
        } else {
          tryAutoplayInSafari();
        }
      } else {
        // For other browsers, try autoplay directly
        playAudio();
      }
    }
    // eslint-disable-next-line
  }, [audioUrl, autoplay, disabled]);

  const tryAutoplayInSafari = async () => {
    if (!audioRef.current) return;
    
    try {
      // Test if we can play without user gesture (in hands-free mode)
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('Safari autoplay succeeded - user gesture permission available');
        setHasUserGesture(true);
        globalSafariUserGesturePermission = true;
        setIsPlaying(true);
        
        // Notify parent component that audio started playing
        if (onPlay && typeof onPlay === 'function') {
          onPlay();
        }
      }
    } catch (error) {
      // Autoplay failed, we need user gesture
      console.log('Safari autoplay blocked, requiring user gesture:', error.name);
      setNeedsUserGesture(true);
      setHasUserGesture(false);
      globalSafariUserGesturePermission = false;
      setIsPlaying(false);
    }
  };

  const playAudio = async () => {
    if (disabled) {
      console.log('Audio playback disabled for non-latest message');
      return;
    }
    
    if (audioRef.current && !isPlaying) {
      setIsPlaying(true);
      audioRef.current.currentTime = 0;
      
      try {
        // Add Safari-specific debugging
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
          console.log('Safari detected - attempting audio playback');
          console.log('Audio source:', audioRef.current.src);
        }
        
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          
          // Clear the user gesture requirement if play was successful
          if (needsUserGesture) {
            setNeedsUserGesture(false);
          }
          
          // Track that we now have user gesture permission
          setHasUserGesture(true);
          globalSafariUserGesturePermission = true;
          
          // Notify parent component that audio started playing
          if (onPlay && typeof onPlay === 'function') {
            onPlay();
          }
        }
      } catch (error) {
        console.error("Error playing audio:", error);
        console.error("Audio src:", audioRef.current?.src);
        console.error("Audio readyState:", audioRef.current?.readyState);
        console.error("Audio networkState:", audioRef.current?.networkState);
        setIsPlaying(false);
        
        // Handle specific Safari autoplay errors
        if (error.name === 'NotAllowedError') {
          console.log('Autoplay blocked - Safari requires user gesture');
          setNeedsUserGesture(true);
          setHasUserGesture(false);
          globalSafariUserGesturePermission = false;
        } else if (error.name === 'NotSupportedError') {
          console.error('Audio format not supported by Safari');
          // Try to fallback or show error message
        }
      }
    }
  };

  const pauseAudio = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      // Notify parent component that audio was paused
      if (onPause && typeof onPause === 'function') {
        onPause();
      }
    }
  };

  const togglePlayback = () => {
    if (disabled) {
      console.log('Audio playback disabled for non-latest message');
      return;
    }
    
    // Clear user gesture requirement when user manually clicks
    if (needsUserGesture) {
      setNeedsUserGesture(false);
    }
    
    // Mark that we now have user gesture permission
    setHasUserGesture(true);
    globalSafariUserGesturePermission = true;
    
    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    // Call the onPause callback to notify parent that audio stopped
    if (onPause && typeof onPause === 'function') {
      onPause();
    }
    // Call the onAudioEnd callback if provided for conversational flow
    if (onAudioEnd && typeof onAudioEnd === 'function') {
      onAudioEnd();
    }
  };

  const handleProgressClick = (e) => {
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickPercent = clickX / rect.width;
      const newTime = clickPercent * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Ensure progress updates after seeking
  const handleSeeked = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Ensure isPlaying state is in sync with audio element
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Generate compact waveform bars like WhatsApp
  const generateWaveformBars = () => {
    const barCount = 30;
    const bars = [];
    
    for (let i = 0; i < barCount; i++) {
      // Create varied heights for visual interest
      const baseHeight = 3;
      const maxHeight = 16;
      const variation = Math.sin(i * 0.5) * 0.5 + 0.5;
      const height = baseHeight + (maxHeight - baseHeight) * variation;
      
      // Highlight played portion
      const isPlayed = (i / barCount) * 100 <= progress;
      
      bars.push(
        <div
          key={i}
          className={`w-1 rounded-full transition-all duration-200 ${
            isPlayed 
              ? 'bg-gray-400/90' 
              : 'bg-white'
          }`}
          style={{ 
            height: `${height}px`,
            minHeight: '3px'
          }}
        />
      );
    }
    
    return bars;
  };

  return (
    <div className="audio-visualizer w-full">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleAudioEnd}
        onSeeked={handleSeeked}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={(e) => {
          console.error('Audio element error:', e.target.error);
          console.error('Error code:', e.target.error?.code);
          console.error('Error message:', e.target.error?.message);
          console.error('Audio src:', e.target.src);
        }}
        onLoadStart={() => console.log('Audio load start')}
        onCanPlay={() => console.log('Audio can play')}
        onCanPlayThrough={() => console.log('Audio can play through')}
        onLoadedData={() => console.log('Audio loaded data')}
        preload="metadata"
      />

      {/* Compact WhatsApp-like layout */}
      <div className="flex items-center space-x-3 py-2">
        {/* Play/Pause Button - More prominent for Safari */}
        <button
          onClick={togglePlayback}
          disabled={loading || disabled}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            disabled 
              ? 'bg-gray-500/30 cursor-not-allowed opacity-50' 
              : loading 
                ? 'bg-white/15' 
                : needsUserGesture
                  ? 'bg-blue-500/70 hover:bg-blue-500/80 shadow-lg animate-pulse hover:scale-110 border-2 border-blue-300/50'
                : isPlaying 
                  ? 'bg-white/40 hover:bg-white/30 hover:scale-105' 
                  : 'bg-white/40 hover:bg-white/25 hover:scale-105'
          }`}
          title={needsUserGesture ? 'Tap to play audio (Safari requires user interaction)' : undefined}
        >
          {loading ? (
            <div className="w-3 h-3 border border-white/60 border-t-transparent rounded-full animate-spin"></div>
          ) : isPlaying ? (
            <FaPause className="text-white/90 text-xs" />
          ) : (
            <FaPlay className={`text-xs ml-0.5 ${needsUserGesture ? 'text-white drop-shadow-sm' : 'text-white/90'}`} />
          )}
        </button>

        {/* Compact Waveform Display */}
        <div 
          className={`flex-1 flex items-center justify-center space-x-0.5 py-1 ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          onClick={disabled ? undefined : handleProgressClick}
        >
          {generateWaveformBars()}
        </div>

        {/* Duration - More compact */}
        <div className={`text-xs font-mono min-w-[32px] text-right ${
          disabled ? 'text-black/40' : 'text-white/90'
        }`}>
          {formatTime(isPlaying ? currentTime : duration)}
        </div>
      </div>
    </div>
  );
});

AudioVisualizer.displayName = 'AudioVisualizer';

AudioVisualizer.propTypes = {
  audioUrl: PropTypes.string,
  audioBytes: PropTypes.string,
  autoplay: PropTypes.bool,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onAudioEnd: PropTypes.func,
  disabled: PropTypes.bool,
};

export default AudioVisualizer;
